import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { Plus, Search, X } from "lucide-react";
import { createBoard, deleteBoard, listBoards } from "./api/boardApi";
import WorkspaceSwitcher from "./components/WorkspaceSwitcher";
import { errorToast, getProjects, successToast } from "../../redux/actionCreator";
import { project_data } from "../../redux/commonReducer";
import { getSession } from "../../utils/session";
import "./boardsList.scss";
import "./workspacesAdmin.scss";

const blankNew = {
  name: "",
  description: "",
  project_id: "",
  visibility: "main",
};

export default function BoardsList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const projects = useSelector(project_data) || [];

  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(blankNew);
  const [workspace, setWorkspace] = useState(null);

  const role = getSession()?.role;
  const isAdmin = role === "admin" || role === "super_admin";

  const load = async (kw = "", wsId = null) => {
    setLoading(true);
    try {
      const res = await listBoards({ keyword: kw, workspace_id: wsId, limit: 100 });
      setBoards(res.data || []);
    } catch (e) {
      errorToast(e.message || "Failed to load boards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workspace?._id) load("", workspace._id);
    if (!projects.length) dispatch(getProjects({ page: 0, limit: 200 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?._id]);

  const handleCreate = async () => {
    const name = draft.name.trim();
    if (!name) return;
    setCreating(true);
    try {
      const body = {
        name,
        description: draft.description,
        visibility: draft.visibility,
        workspace_id: workspace?._id,
      };
      if (draft.project_id) body.project_id = draft.project_id;
      const res = await createBoard(body);
      successToast("Board created");
      setShowCreate(false);
      setDraft(blankNew);
      navigate(`/boards/${res.data._id}`);
    } catch (e) {
      errorToast(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, boardId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this board and all its items?")) return;
    try {
      await deleteBoard(boardId);
      setBoards((bs) => bs.filter((b) => b._id !== boardId));
      successToast("Board deleted");
    } catch (err) {
      errorToast(err.message);
    }
  };

  const canManage = (b) => {
    if (role === "admin" || role === "super_admin") return true;
    const me = getSession()?._id;
    return String(b.owner) === String(me);
  };

  return (
    <div className="boards-list-page">
      <header className="boards-head">
        <div className="boards-head-left">
          <WorkspaceSwitcher
            value={workspace}
            onChange={setWorkspace}
            onManageClick={isAdmin ? () => navigate("/workspaces") : null}
          />
        </div>
        <div className="boards-head-actions">
          <Form
            className="boards-search"
            onSubmit={(e) => {
              e.preventDefault();
              load(keyword);
            }}
          >
            <div className="search-box">
              <span className="search-icon"><Search size={16} /></span>
              <Form.Control
                placeholder="Search boards..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              {keyword && (
                <button
                  type="button"
                  className="search-clear"
                  title="Clear"
                  onClick={() => {
                    setKeyword("");
                    load("", workspace?._id);
                  }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <Button variant="outline-secondary" type="submit">Search</Button>
          </Form>
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="me-1" /> New Board
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="boards-loading"><Spinner animation="border" size="sm" /> Loading…</div>
      ) : boards.length === 0 ? (
        <div className="boards-empty">
          <p>No boards yet.</p>
          <Button variant="primary" onClick={() => setShowCreate(true)}>Create your first board</Button>
        </div>
      ) : (
        <div className="boards-grid">
          {boards.map((b) => {
            const project = projects.find((p) => String(p._id) === String(b.project_id));
            return (
              <div
                key={b._id}
                className="board-card"
                onClick={() => navigate(`/boards/${b._id}`)}
              >
                <div className="board-card-head">
                  <h4>{b.name}</h4>
                  {canManage(b) && (
                    <button
                      className="board-card-del"
                      onClick={(e) => handleDelete(e, b._id)}
                      title="Delete"
                      aria-label="Delete board"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {b.description && <p className="board-card-desc">{b.description}</p>}
                <div className="board-card-meta">
                  <span className={`vis-tag vis-${b.visibility}`}>{b.visibility}</span>
                  {project && <span className="project-tag">{project.project_name}</span>}
                  <span className="member-count">{(b.members || []).length} member(s)</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton><Modal.Title>New board</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              autoFocus
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="e.g. Q3 launch plan"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Link to project (optional)</Form.Label>
            <Form.Select
              value={draft.project_id}
              onChange={(e) => setDraft((d) => ({ ...d, project_id: e.target.value }))}
            >
              <option value="">— standalone board —</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>{p.project_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Visibility</Form.Label>
            <Form.Select
              value={draft.visibility}
              onChange={(e) => setDraft((d) => ({ ...d, visibility: e.target.value }))}
            >
              <option value="main">Main — everyone in the workspace can see it</option>
              <option value="private">Private — only invited members</option>
              <option value="shareable">Shareable — invite link (Phase 5)</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} disabled={creating || !draft.name.trim()}>
            {creating ? "Creating…" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
